<template>
    <div ref="container" class="custom-chart-container p-compare-reserves__d3 u-position-relative">
        <div class="chart-tooltip" ref="tooltip">
            <strong>TITLE</strong>
            <br>
            <strong>COUNT</strong> claims
        </div>
        <svg class="custom-chart" ref="svg" :height="height" :width="width"></svg>
    </div>
</template>

<script>
    import * as d3 from 'd3';

    const MIN_HEIGHT = 400;
    const MAX_WIDTH = 3000;
    const DURATION = 500;

    const BAR_PADDING = 0.1;

    export default {
        name: 'range-chart',
        props: {
            claims: {
                type: Array,
                required: true
            },
            range: {
                type: Array,
                required: true
            },
        },
        data() {
            return {
                width: 0,
                height: 0,
                margin: {top: 20, right: 20, bottom: 50, left: 65},
                xScaleLinear: null,
                xScaleBand: null,
                yScale: null,
                svg: null,
                tooltip: null,
                drawType: 'bar',
                groups: {
                    axis: {
                        x: null,
                        y: null
                    },
                    marks: null,
                    rangeMarks: null,
                    boundBox: null
                }
            }
        },
        computed: {
            /**
             * Remove any claims that do not have a % or infinite percentage.
             */
            cleanClaims() {
                return this.claims.filter(claim => claim.percent !== Infinity && !isNaN(claim.percent));
            },
            /**
             * Min. Percentage
             */
            min() {
                return this.cleanClaims.reduce((currMin, claim) => claim.percent < currMin ? claim.percent : currMin, 1);
            },
            /**
             * Max. Percentage
             */
            max() {
                return this.cleanClaims.reduce((currMax, claim) => claim.percent > currMax ? claim.percent : currMax, -1);
            },
            /**
             * All claims reduced to the round percentage and for the only the spread of claims.
             *
             * @return {{percent: number, count: number}[]}
             */
            results() {
                let results = Array.apply(null, Array(20))
                    .map((val, index) => ({percent: ((index * 10) - 100) / 100, count: 0}));

                this.cleanClaims.forEach(claim => {
                    let index = Math.floor((claim.percent + 1) * 10);
                    if (index < 0) {
                        index = 0;
                    }
                    if (index > 19) {
                        index = 19;
                    }
                    results[index].count++;
                });
                return results;
            },
            /**
             * Max. number of claims.
             */
            maxClaims() {
                return this.results.reduce((count, d) => count + d.count, 0);
            }
        },
        mounted() {
            // Wrap svg
            this.svg = d3.select(this.$refs.svg);
            // Wrap tooltip
            this.tooltip = d3.select(this.$refs.tooltip);
            // Do the setup
            this.init();
            // Listen for resize
            window.addEventListener('resize', this.reset);
        },
        destroyed() {
            window.removeEventListener('resize', this.reset);
        },
        /**
         * Track items to trigger re-draw
         */
        watch: {
            min() {
                this.draw();
            },
            max() {
                this.draw();
            },
            range() {
                this.draw();
            }
        },
        methods: {
            /**
             * Setup charts
             */
            init() {
                this.determineHeightAndWidth();
                this.setupScales();
                this.setupGroups();
                this.setupAxisLabels();
                this.draw();
            },
            /**
             * Determine height and width form the parent container.
             */
            determineHeightAndWidth() {
                let container = d3.select(this.$refs.container);
                let containerWidth = parseFloat(container.style('width').replace('px', ''));
                let containerHeight = parseFloat(container.style('height').replace('px', ''));

                this.width = Math.min(MAX_WIDTH, containerWidth);
                this.height = Math.max(MIN_HEIGHT, containerHeight);
            },
            /**
             * Setup the scales.
             */
            setupScales() {

                this.xScaleLinear = d3.scaleLinear()
                    .range([0, this.width - this.margin.left - this.margin.right - (this.results.length * BAR_PADDING)]);

                this.xScaleBand = d3.scaleBand()
                    .range([0, this.width - this.margin.left - this.margin.right - 2])
                    .padding(BAR_PADDING);
                this.yScale = d3.scaleLinear()
                    .range([this.height - this.margin.top - this.margin.bottom, 0]);
                this.applyDomainToScale();
            },
            /**
             * Setup the groups and position them.
             */
            setupGroups() {
                this.svg.selectAll('g').remove();
                this.groups.axis.x = this.svg
                    .append('g')
                    .attr("transform", "translate(" + this.margin.left + "," + (this.height - this.margin.bottom) + ")")
                    .attr('class', 'x axis');
                this.groups.axis.y = this.svg
                    .append('g')
                    .attr("transform", "translate(" + this.margin.left + "," + (this.margin.top) + ")")
                    .attr('class', 'y axis');
                this.groups.boundBox = this.svg
                    .append('g')
                    .attr('class', 'bound-box')
                    .attr("transform", "translate(" + this.margin.left + "," + (this.margin.top) + ")");
                this.groups.rangeMarks = this.svg
                    .append('g')
                    .attr('class', 'range-marks')
                    .attr("transform", "translate(" + this.margin.left + "," + this.margin.top + ")");
                this.groups.marks = this.svg
                    .append('g')
                    .attr('class', 'marks')
                    .attr("transform", "translate(" + this.margin.left + "," + (this.margin.top) + ")");
            },
            /**
             * Update scale with domain values.
             */
            applyDomainToScale: function () {
                this.xScaleLinear.domain([-1, 1]);
                this.xScaleBand.domain(this.results.map(result => result.percent));
                this.yScale.domain([0, this.maxClaims + 1]);
            },
            /**
             * Apply x and y axis labels.
             */
            setupAxisLabels() {
                this.svg.append('g')
                    .attr("transform", "translate(" + this.margin.left + "," + (this.margin.top) + ")")
                    .attr('class', 'y axis-label')
                    .append('text')
                    .attr("transform", "rotate(-90)")
                    .attr("y", 0 - (this.margin.left / 2))
                    .attr("x", 0 - (this.height / 2))
                    .text("Number of Claims");
                this.svg.append('g')
                    .attr("transform", "translate(0," + (this.height - this.margin.bottom) + ")")
                    .attr('class', 'x axis-label')
                    .append('text')
                    .style("text-anchor", "middle")
                    .attr("y", this.margin.bottom)
                    .attr("x", (this.width / 2))
                    .text("Percentile");
            },
            /**
             * Do all drawing functions.
             */
            draw() {
                this.drawAxis();
                switch (this.drawType) {
                    case 'mark':
                        this.drawMarks();
                        break;
                    case 'line':
                        this.drawLine();
                        break;
                    case 'bar':
                        this.drawBars();
                        break;
                }
                this.drawRangeMarkers();
            },
            /**
             * Animate the axis to domain.
             */
            drawAxis() {
                let xGroup = this.groups.axis.x;
                let yGroup = this.groups.axis.y;
                this.applyDomainToScale();
                const percentFormat = d3.format(".0%");
                xGroup
                    .transition()
                    .duration(DURATION)
                    .call(d3.axisBottom(this.drawType === 'bar' ? this.xScaleBand : this.xScaleLinear)
                        .tickFormat(d => percentFormat(d) + ' to ' + percentFormat(d + 0.1)));
                yGroup
                    .transition()
                    .duration(DURATION)
                    .call(d3.axisLeft(this.yScale)
                        .tickFormat(d => Math.floor(d) !== d ? null : d));
            },
            /**
             * Draw the circle marks.
             */
            drawMarks() {
                this.applyDomainToScale();

                const marks = this.groups
                    .marks
                    .selectAll('.mark')
                    .data(this.results);
                const determineClass = prefix => d => {
                    if (d.percent < this.range[0]) {
                        return prefix + ' under';
                    }
                    if (d.percent > this.range[1]) {
                        return prefix + ' over';
                    }
                    return prefix + ' centre';
                };
                marks.enter()
                    .append('circle')
                    .attr('class', determineClass('mark'))
                    .attr('r', 2)
                    .attr("cx", d => this.xScaleLinear(d.percent))
                    .attr("cy", () => this.yScale(0))
                    .transition()
                    .duration(DURATION)
                    .attr("cy", d => this.yScale(d.count));
                marks
                    .attr('class', determineClass('mark'))
                    .transition()
                    .duration(DURATION)
                    .attr("cx", d => this.xScaleLinear(d.percent))
                    .attr("cy", d => this.yScale(d.count));
                marks
                    .exit()
                    .remove();
            },
            /**
             * Draw the circle marks.
             */
            drawBars() {
                this.applyDomainToScale();

                const bars = this.groups
                    .marks
                    .selectAll('.bar')
                    .data(this.results);
                const determineClass = prefix => d => {
                    if (d.percent < this.range[0]) {
                        return prefix + ' under';
                    }
                    if (d.percent >= this.range[1]) {
                        return prefix + ' over';
                    }
                    return prefix + ' centre';
                };
                const innerHeight = this.height - this.margin.top - this.margin.bottom;
                bars.enter()
                    .append('rect')
                    .attr('class', determineClass('bar'))
                    .attr('x', d => this.xScaleBand(d.percent))
                    .attr("width", this.xScaleBand.bandwidth())
                    .attr("height", innerHeight - this.yScale(0))
                    .attr("y", this.yScale(0))
                    .on("mouseover", this.showTooltip.bind(this))
                    .on("mouseout", this.hideTooltip.bind(this))
                    .transition()
                    .duration(DURATION)
                    .attr("height", d => innerHeight - this.yScale(d.count))
                    .attr("y", d => this.yScale(d.count));

                bars
                    .attr('class', determineClass('bar'))
                    .transition()
                    .duration(DURATION)
                    .attr("height", d => innerHeight - this.yScale(d.count))
                    .attr("y", d => this.yScale(d.count));

                bars
                    .exit()
                    .remove();
            },
            /**
             * Draw a line to follow the marks.
             */
            drawLine() {
                const lineValue = d3.line()
                    .x(d => this.xScaleLinear(d.percent))
                    .y(d => this.yScale(d.count));
                const lineZero = d3.line()
                    .x(d => this.xScaleLinear(d.percent))
                    .y(() => this.yScale(0));

                const line = this.groups
                    .marks
                    .selectAll('.line')
                    .data([this.results]);

                line.enter()
                    .append('path').attr('class', 'line')
                    .attr('d', lineZero)
                    .transition()
                    .duration(DURATION)
                    .attr("d", lineValue);

                line.transition()
                    .duration(DURATION)
                    .attr("d", lineValue);

                line.exit().remove();
            },
            /**
             * Draw the upright range markers.
             */
            drawRangeMarkers() {
                const applyToMakers = (lower, upper) => (d, index) => index === 0 ? lower(d) : upper(d);

                const markerLines = this.groups.rangeMarks
                    .selectAll('line')
                    .data(this.range);

                markerLines.enter()
                    .append('line')
                    .attr('class', applyToMakers(() => 'under', () => 'over'))
                    .attr('x1', this.xScaleLinear)
                    .attr('y1', this.yScale(0))
                    .attr('x2', this.xScaleLinear)
                    .attr('y2', this.yScale(this.maxClaims + 1));

                markerLines
                    .attr('x1', this.xScaleLinear)
                    .attr('x2', this.xScaleLinear);

                const boundBox = this.groups.boundBox
                    .selectAll('rect')
                    .data([
                        {start: -1, end: this.range[0], name: 'under'},
                        {start: this.range[0], end: this.range[1], name: 'centre'},
                        {start: this.range[1], end: 1, name: 'over'},
                    ]);
                boundBox.enter()
                    .append('rect')
                    .attr('class', d => 'range-bound ' + d.name)
                    .attr('x', d => this.xScaleLinear(d.start))
                    .attr("width", d => this.xScaleLinear(d.end) - this.xScaleLinear(d.start))
                    .attr("height", this.yScale(0) - this.yScale(this.maxClaims + 1))
                    .attr("y", this.yScale(this.maxClaims + 1));
                boundBox
                    .attr('x', d => this.xScaleLinear(d.start))
                    .attr("width", d => this.xScaleLinear(d.end) - this.xScaleLinear(d.start))
            },
            showTooltip(item) {
                const percentFormat = d3.format(".2%");
                this.tooltip
                    .html(
                        `<strong>${percentFormat(item.percent)} to ${percentFormat(item.percent + 0.1)}</strong>
                        <br>
                        <strong>${item.count}</strong> claims`
                    );
                const evt = d3.event;
                let left = this.margin.left +
                    (parseFloat(d3.select(evt.currentTarget).attr("x"))) +
                    (this.xScaleBand.step() / 2);
                left -= parseFloat(this.tooltip.style('width').replace("px", '') / 2);

                this.tooltip
                    .style("visibility", "visible")
                    .style("left", left + "px")
                    .style("top", (this.yScale(item.count) - 70) + "px")
                    .transition()
                    .duration(DURATION / 2)
                    .style("opacity", 1);
            },
            hideTooltip() {
                this.tooltip.transition()
                    .duration(DURATION / 2)
                    .style("opacity", 0);
            },
            reset() {
                this.groups.axis.x.remove();
                this.groups.axis.y.remove();
                this.groups.marks.remove();
                this.groups.rangeMarks.remove();
                this.groups.boundBox.remove();
                if (this.$refs && this.$refs.container) {
                    this.init();
                }
            }
        }
    }
</script>

<style lang="scss">
    $over-colour: #d94c8b;
    $under-colour: #3df2c2;
    .chart-tooltip {
        opacity: 0;
        position: absolute;
        width: 120px;
        background-color: #555;
        color: #fff;
        text-align: center;
        padding: 5px 0;
        border-radius: 6px;
        z-index: 1;
        transition: opacity .6s;
        visibility: hidden;
        &:after {
            content: "";
            position: absolute;
            top: 100%;
            left: 50%;
            margin-left: -7.5px;
            border: 5px solid transparent;
            border-top-color: #555;
        }
    }

    .custom-chart {
        font-family: 'Nunito', sans-serif;
        // All custom lines
        .line {
            fill: none; // have no fill
        }
        .marks { // Scatter plot area
            /** For line draw mode **/
            .line { // The line
                stroke: black;
            }
            /** For scatter draw mode **/
            .mark { // The spots
                stroke: black;
                stroke-width: 2;
                fill: #EEE;
                r: 5
            }
            .mark.under {
                stroke: $under-colour;
            }
            .mark.over {
                stroke: $over-colour;
            }
            /** For bar chart draw mode **/
            .bar {
                opacity: 0.7;
                stroke-width: 0;
                fill: #7f7f7f;
            }
            .bar.over {
                fill: lighten($over-colour, 15%);
            }
            .bar.under {
                fill: lighten($under-colour, 15%);
            }
        }
        .range-marks { // Range markers
            line {
                stroke: black;
            }
            .under {
                stroke: $under-colour;
            }
            .over {
                stroke: $over-colour;
            }
        }
        .bound-box { // The boxes at the back of the chart
            rect {
                opacity: 0.1;
                fill: none;
            }
            .under {
                fill: $under-colour;
            }
            .over {
                fill: $over-colour;
            }
        }
        .axis { //Both Axis
            path { // Main axis line (these can all be applied to individual axis)
                stroke: #e1e1e1;
            }
            .tick { // Individual tick
                line { // The line
                    stroke: none;
                }
                text { // The text element
                    fill: #7f7f7f;
                }
            }
        }
        .x.axis { // X axis
            text {
                font-size: 9px;
            }
        }
        .y.axis { // Y axis
            text {
                font-size: 16px;
            }
        }
    }
</style>


// WEBPACK FOOTER //
// RangeChart.vue?388e1f6e